/**
 * /tracking/coupons — coupon code tracking workspace (feature #171).
 *
 * Declares the TanStack Router route and delegates rendering to
 * `CouponTrackingPage`. The flat-file naming convention uses dots as path
 * separators, so `tracking.coupons.tsx` maps to `/tracking/coupons`.
 *
 * Search params:
 *   ?mode=general|exclusive|unique   Pre-filter the list.
 *   ?couponId=cpn_xxx                Focus a specific coupon (opens its
 *                                     redemption history panel).
 */

import { createRoute } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import {
	COUPON_MODES,
	type CouponMode,
} from "@/features/coupon-tracking/api";
import { CouponTrackingPage } from "@/features/coupon-tracking/CouponTrackingPage";

// ---------------------------------------------------------------------------
// Search validation
// ---------------------------------------------------------------------------

interface TrackingCouponsSearch {
	mode?: CouponMode;
	couponId?: string;
}

function coerceMode(raw: unknown): CouponMode | undefined {
	if (typeof raw !== "string") return undefined;
	const trimmed = raw.trim().toLowerCase();
	if ((COUPON_MODES as readonly string[]).includes(trimmed)) {
		return trimmed as CouponMode;
	}
	return undefined;
}

function coerceCouponId(raw: unknown): string | undefined {
	if (typeof raw !== "string") return undefined;
	const trimmed = raw.trim();
	if (!trimmed || trimmed.length > 120) return undefined;
	return trimmed;
}

// ---------------------------------------------------------------------------
// Route — mounts at `/tracking/coupons`.
// ---------------------------------------------------------------------------

export const trackingCouponsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/tracking/coupons",
	validateSearch: (raw: Record<string, unknown>): TrackingCouponsSearch => {
		const mode = coerceMode(raw.mode);
		const couponId = coerceCouponId(raw.couponId);
		const out: TrackingCouponsSearch = {};
		if (mode) out.mode = mode;
		if (couponId) out.couponId = couponId;
		return out;
	},
	component: TrackingCouponsRoutePage,
});

export default trackingCouponsRoute;

// Wrapper reads the search params so the page can deep-link without prop
// drilling from the router call site.
export function TrackingCouponsRoutePage() {
	const { mode, couponId } = trackingCouponsRoute.useSearch();
	return <CouponTrackingPage initialMode={mode} initialCouponId={couponId} />;
}

// Re-export for the wire-up cycle / root registry import.
export { CouponTrackingPage } from "@/features/coupon-tracking/CouponTrackingPage";
